import { CheckIcon, DownloadIcon, FileArchiveIcon, Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  type DownloadManifest,
  episodeDownloadUrl,
  getDownloadManifest,
  packDownloadUrl,
} from "../lib/api";
import { formatBytes } from "../lib/file-groups";
import { cn } from "../lib/utils";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { LoadingNote } from "./ui/loading";
import { SkeletonRows } from "./ui/skeleton";
import { Switch } from "./ui/switch";

/**
 * Okno pobierania zgromadzonych odcinków.
 *
 * Pokazuje **tylko to, co u nas leży** — manifest patrzy na dysk, nie na katalog.
 * Odcinek, którego nie mamy, nie ma się tu z czego wziąć: propozycja pobrania czegoś,
 * co najpierw trzeba zdobyć ze źródła, byłaby obietnicą innego mechanizmu (a ten
 * mieszka na stronie tytułu, przy „Przygotuj serię").
 *
 * Jeden wybrany odcinek idzie **jako plik** (`mp4`/`mkv`), kilka albo wszystkie —
 * **jako ZIP**. Rozstrzyga to liczba zaznaczeń, a nie osobny przełącznik: archiwum
 * z jednym plikiem w środku jest krokiem, który nikomu nie służy, a lista wyborów
 * krótsza o jeden jest łatwiejsza do przejścia pilotem.
 */
export function DownloadDialog({
  titleId,
  open,
  onOpenChange,
}: {
  titleId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [manifest, setManifest] = useState<DownloadManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [withSubtitles, setWithSubtitles] = useState(true);
  /**
   * Numer sezonu do nazw `SxxExx`, albo `null` dla `Exx`.
   *
   * Pole, nie domysł: katalog zna porę roku i rok (`spring 2026`), a **nie** numer
   * sezonu w serii. Zły numer jest gorszy od jego braku — Plex i Jellyfin zapiszą go
   * jako fakt i odcinek wyląduje pod obcym sezonem. Kto pobiera, ten wie, co bierze.
   */
  const [seasonNumber, setSeasonNumber] = useState<number | null>(null);
  /**
   * Dograć brakujące napisy z animesub.info.
   *
   * Domyślnie wyłączone: to dwa żądania do obcego serwisu na każdy odcinek bez
   * napisów. Ma się dziać, gdy ktoś tego chce, a nie przy każdym „pobierz serię".
   */
  const [fill, setFill] = useState(false);

  /**
   * Manifest pobieramy przy OTWARCIU, nie przy montażu strony.
   *
   * Sprawdzenie dotyka dysku dla każdego odcinka serii, a strona tytułu otwiera się
   * znacznie częściej niż to okno.
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    getDownloadManifest(titleId)
      .then((data) => {
        if (cancelled) return;
        setManifest(data);
        // Domyślnie wszystko, co jest — to najczęstszy wybór („weź mi tę serię").
        setSelected(
          new Set(
            data.episodes
              .map((episode) => episode.episodeNumber)
              .filter((number): number is number => number !== null),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setError("Nie udało się sprawdzić, co jest na dysku");
      });
    return () => {
      cancelled = true;
    };
  }, [open, titleId]);

  const episodes = manifest?.episodes ?? [];
  const chosen = episodes.filter(
    (episode) => episode.episodeNumber !== null && selected.has(episode.episodeNumber),
  );
  const totalBytes = chosen.reduce((sum, episode) => sum + (episode.sizeBytes ?? 0), 0);
  const anySubtitles = chosen.some((episode) => episode.subtitles.length > 0);
  /** Ile z wybranych odcinków nie ma ani jednej ścieżki napisów. */
  const missingSubtitles = chosen.filter((episode) => episode.subtitles.length === 0).length;
  /** Jeden odcinek to plik, więcej niż jeden — archiwum. */
  const single = chosen.length === 1 ? (chosen[0] ?? null) : null;

  function toggle(episodeNumber: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(episodeNumber)) next.delete(episodeNumber);
      else next.add(episodeNumber);
      return next;
    });
  }

  const href =
    single !== null
      ? episodeDownloadUrl(single.mediaFileId, seasonNumber)
      : packDownloadUrl({
          seasonNumber,
          fillSubtitles: fill,
          titleId,
          // Wszystkie zaznaczone to to samo co „brak listy", ale jawna lista jest
          // czytelna w dzienniku serwera i nie zmienia się pod nami między kliknięciem
          // a żądaniem.
          episodes: chosen
            .map((episode) => episode.episodeNumber)
            .filter((number): number is number => number !== null),
          withSubtitles,
        });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogTitle>Pobierz na dysk</AlertDialogTitle>
        <AlertDialogDescription>
          {manifest === null
            ? "Odcinki tej serii, które są już na serwerze."
            : `${manifest.titleName} — ${episodes.length} ${episodes.length === 1 ? "odcinek" : "odcinków"} u nas`}
        </AlertDialogDescription>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        {manifest === null && error === null && (
          <div className="flex flex-col gap-3">
            <LoadingNote label="Sprawdzam, co jest na dysku…" />
            <SkeletonRows count={4} />
          </div>
        )}

        {manifest !== null && episodes.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nic z tej serii nie jest jeszcze pobrane. Zdobądź odcinki na stronie tytułu — wtedy
            będzie co zabrać.
          </p>
        )}

        {episodes.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setSelected(
                    new Set(
                      episodes
                        .map((episode) => episode.episodeNumber)
                        .filter((number): number is number => number !== null),
                    ),
                  )
                }
              >
                Zaznacz wszystkie
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                Odznacz wszystkie
              </Button>
            </div>

            {/* Lista z własnym przewijaniem: dwadzieścia cztery odcinki nie mogą
                rozepchnąć okna poza ekran. */}
            <ul className="max-h-72 overflow-y-auto rounded-lg border border-border">
              {episodes.map((episode) => {
                const number = episode.episodeNumber;
                const active = number !== null && selected.has(number);
                return (
                  <li key={episode.mediaFileId}>
                    <button
                      type="button"
                      disabled={number === null}
                      onClick={() => number !== null && toggle(number)}
                      className={cn(
                        "focus-ring-own flex w-full items-center gap-3 px-3 py-2 text-left text-sm outline-none",
                        "focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50",
                        active ? "bg-secondary/60" : "hover:bg-secondary/30",
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          "flex size-4 shrink-0 items-center justify-center rounded border",
                          active
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border",
                        )}
                      >
                        {active && <CheckIcon className="size-3" />}
                      </span>
                      <span className="font-medium">
                        {number === null ? "Materiał bez numeru" : `Odcinek ${number}`}
                      </span>
                      <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="uppercase">{episode.extension}</span>
                        {episode.sizeBytes !== null && (
                          <span>{formatBytes(episode.sizeBytes)}</span>
                        )}
                        {/* Napisy widać PRZED pobraniem, bo od nich zależy, czy paczka
                            ma sens bez nich — materiał z polskich źródeł bywa
                            z napisami wtopionymi. */}
                        {episode.subtitles.length > 0 && (
                          <span className="text-accent">
                            napisy: {episode.subtitles.map((track) => track.lang).join(", ")}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Primityw nosi etykietę i podpowiedź sam — własny `<label>` wokół niego
                dałby dwie etykiety dla jednej kontrolki. */}
            <Switch
              label="Dołącz pliki napisów"
              hint={
                anySubtitles
                  ? "Wejdą obok materiału, z tą samą nazwą — odtwarzacze wczytają je same."
                  : "Wybrane odcinki nie mają osobnych plików napisów."
              }
              checked={withSubtitles && anySubtitles}
              disabled={!anySubtitles || single !== null}
              onChange={(event) => setWithSubtitles(event.currentTarget.checked)}
            />

            {single !== null && anySubtitles && (
              <p className="text-xs text-muted-foreground">
                Pojedynczy odcinek idzie jako sam plik wideo. Zaznacz drugi, żeby dostać archiwum
                razem z napisami.
              </p>
            )}

            {/*
              Uzupełnianie napisów z animesuba — „gotowa paczka release".
              Widoczne tylko dla archiwum: przy jednym pliku wideo nie ma czego
              dołożyć, bo pojedynczy odcinek idzie bez plików obok.
            */}
            {single === null && (
              <Switch
                label="Dograj brakujące napisy z animesub.info"
                hint={
                  missingSubtitles === 0
                    ? "Wszystkie wybrane odcinki mają już napisy."
                    : `${missingSubtitles} ${missingSubtitles === 1 ? "odcinek" : "odcinków"} bez napisów — spróbujemy je znaleźć przed spakowaniem. Potrwa kilka sekund na odcinek.`
                }
                checked={fill && missingSubtitles > 0}
                disabled={missingSubtitles === 0 || !withSubtitles}
                onChange={(event) => setFill(event.currentTarget.checked)}
              />
            )}

            {/*
              Schemat nazw. `SxxExx` jest tym, po czym Plex i Jellyfin rozpoznają
              odcinek — ale numeru sezonu nie zgadujemy: katalog ma porę roku i rok,
              nie pozycję w serii. Zły numer trafiłby do biblioteki jako fakt.
            */}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-muted-foreground">Nazwy plików</span>
              <Button
                variant={seasonNumber === null ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setSeasonNumber(null)}
              >
                Odcinek 07
              </Button>
              <Button
                variant={seasonNumber !== null ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setSeasonNumber((prev) => prev ?? 1)}
              >
                S01E07 (Plex, Jellyfin)
              </Button>
              {seasonNumber !== null && (
                <label className="flex items-center gap-2">
                  <span className="text-muted-foreground">sezon</span>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={seasonNumber}
                    onChange={(event) => {
                      const next = Number.parseInt(event.currentTarget.value, 10);
                      // Puste pole i śmieci nie mogą dać `NaN` w adresie: serwer
                      // odrzuciłby całe żądanie walidacją, zamiast pobrać cokolwiek.
                      setSeasonNumber(Number.isInteger(next) ? Math.min(99, Math.max(1, next)) : 1);
                    }}
                    className="focus-ring-own w-16 rounded-md border border-border bg-card px-2 py-1 tabular-nums outline-none focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
                  />
                </label>
              )}
            </div>
          </>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Zamknij</AlertDialogCancel>
          {/*
            Zwykły odnośnik, nie `fetch`: pobieranie ma iść przez menedżer przeglądarki,
            który pokazuje postęp, wznawia i zapisuje tam, gdzie widz chce. `fetch`
            plus `blob:` trzymałby całą serię w pamięci karty.
          */}
          <Button asChild disabled={chosen.length === 0} variant="hero">
            <a href={href} download aria-disabled={chosen.length === 0}>
              {chosen.length === 0 ? (
                <>
                  <DownloadIcon />
                  Wybierz odcinki
                </>
              ) : single !== null ? (
                <>
                  <DownloadIcon />
                  Pobierz {single.extension.toUpperCase()}
                  {single.sizeBytes !== null && ` · ${formatBytes(single.sizeBytes)}`}
                </>
              ) : (
                <>
                  <FileArchiveIcon />
                  Pobierz ZIP · {chosen.length} odc.
                  {totalBytes > 0 && ` · ~${formatBytes(totalBytes)}`}
                </>
              )}
            </a>
          </Button>
        </AlertDialogFooter>

        {manifest === null && error === null && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" />
            Czytam katalog materiału…
          </p>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
